/**
 * Cache adapter interfaces for pluggable caching strategies
 *
 * This module defines the contract for cache implementations, allowing users
 * to provide custom caching backends (Redis, Cloudflare KV, localStorage, etc.)
 * while maintaining a consistent API.
 */

/**
 * Core cache adapter interface with essential operations
 *
 * All methods are async to support both local (Map, localStorage) and remote
 * (Redis, Cloudflare KV) cache implementations.
 *
 * @template T - Type of values stored in the cache
 *
 * @example
 * ```typescript
 * // Implement custom cache adapter
 * class RedisCacheAdapter implements CacheAdapter<MyData> {
 *   async get(key: string): Promise<MyData | undefined> {
 *     const value = await redis.get(key);
 *     return value ? JSON.parse(value) : undefined;
 *   }
 *
 *   async set(key: string, value: MyData, ttl?: number): Promise<void> {
 *     await redis.setEx(key, ttl ?? 900, JSON.stringify(value));
 *   }
 *
 *   // ... implement other methods
 * }
 * ```
 */
export interface CacheAdapter<T> {
  /**
   * Retrieves a value from the cache
   *
   * @param key - Cache key
   * @returns Cached value or undefined if not found or expired
   */
  get(key: string): Promise<T | undefined>;

  /**
   * Stores a value in the cache
   *
   * @param key - Cache key
   * @param value - Value to cache
   * @param ttl - Optional time-to-live in seconds (overrides default TTL)
   */
  set(key: string, value: T, ttl?: number): Promise<void>;

  /**
   * Removes a specific entry from the cache
   *
   * @param key - Cache key to delete
   * @returns true if key was deleted, false if it didn't exist
   */
  delete(key: string): Promise<boolean>;

  /**
   * Clears all entries from the cache
   */
  clear(): Promise<void>;

  /**
   * Checks if a key exists in the cache and is not expired
   *
   * @param key - Cache key
   * @returns true if key exists and is valid
   */
  has(key: string): Promise<boolean>;
}

/**
 * Cache statistics information
 *
 * Not all cache implementations can provide all statistics.
 * Optional fields allow adapters to return only what they can track.
 */
export interface CacheStats {
  /** Current number of cache entries (if trackable) */
  size?: number;

  /** Whether caching is enabled */
  enabled: boolean;

  /** Time-to-live in milliseconds (if applicable) */
  ttl?: number;

  /** Maximum cache size before eviction (if applicable) */
  maxSize?: number;
}

/**
 * Extended cache adapter interface with statistics and maintenance operations
 *
 * This interface is optional - not all cache implementations can provide
 * statistics or manual cleanup operations. Implementations should only
 * implement this if they can meaningfully support these operations.
 *
 * @template T - Type of values stored in the cache
 *
 * @example
 * ```typescript
 * class MemoryCacheAdapter implements CacheAdapterWithStats<MyData> {
 *   async getStats(): Promise<CacheStats> {
 *     return {
 *       size: this.cache.size,
 *       enabled: true,
 *       ttl: this.ttl,
 *       maxSize: this.maxSize
 *     };
 *   }
 *
 *   async prune(): Promise<number> {
 *     return this.cache.removeExpired();
 *   }
 * }
 * ```
 */
export interface CacheAdapterWithStats<T> extends CacheAdapter<T> {
  /**
   * Gets cache statistics
   *
   * @returns Cache statistics object
   */
  getStats(): Promise<CacheStats>;

  /**
   * Removes expired entries from the cache (manual cleanup)
   *
   * This is optional because some caches (Redis, Cloudflare KV) handle
   * expiration automatically and don't need manual pruning.
   *
   * @returns Number of entries removed
   */
  prune?(): Promise<number>;
}

/**
 * Type guard to check if a cache adapter supports statistics
 *
 * @param adapter - Cache adapter to check
 * @returns true if adapter implements CacheAdapterWithStats
 *
 * @example
 * ```typescript
 * if (isCacheAdapterWithStats(cache)) {
 *   const stats = await cache.getStats();
 *   console.log(`Cache size: ${stats.size}`);
 * }
 * ```
 */
export function isCacheAdapterWithStats<T>(
  adapter: CacheAdapter<T>
): adapter is CacheAdapterWithStats<T> {
  return 'getStats' in adapter && typeof adapter.getStats === 'function';
}
